<template>
    <div :style="{ width: `${diameter}px`, height: `${diameter}px` }" class="circled-graph">
        <canvas :width="diameter" :height="diameter" ref="background"></canvas>
        <p v-if="displayPercent">{{ value }}%</p>
        <p v-else>
            <slot></slot>
        </p>
        <svg
            :style="{ width: `${diameter}px`, height: `${diameter}px` }"
            v-if="needGraph"
            :width="diameter"
            :height="diameter"
        >
            <path :d="path" stroke-linecap="round" id="arc" fill="none" :stroke="stroke" :stroke-width="strokeWidth" />
        </svg>
    </div>
</template>

<script>
import { gsap } from "gsap"

export default {
    props: {
        stroke: {
            type: String,
            default: "#15A06E"
        },
        value: {
            type: Number,
            required: false,
            default: 0
        },
        strokeWidth: {
            type: Number,
            required: false,
            default: 0
        },
        diameter: {
            type: Number,
            required: false,
            default: 0
        },
        needGraph: {
            type: Boolean,
            required: false,
            default: false
        },
        displayPercent: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    name: "CircledGraph",
    data() {
        return {
            lastAngle: 0,
            angle: {
                degrees: 0 - Math.PI / 2
            },
            path: ""
        }
    },

    methods: {
        describeArc(x, y, radius, startAngle, endAngle) {
            const start = this.polarToCartesian(x, y, radius, endAngle)
            const end = this.polarToCartesian(x, y, radius, startAngle)

            const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1"

            return ["M", start.x, start.y, "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y].join(" ")
        },

        polarToCartesian(centerX, centerY, radius, angleInDegrees) {
            const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0

            return {
                x: centerX + radius * Math.cos(angleInRadians),
                y: centerY + radius * Math.sin(angleInRadians)
            }
        },

        convertDegrees(degrees) {
            return degrees * (Math.PI / 180) - Math.PI / 2
        },

        animateGraph() {
            if (!this.value) {
                return
            }
            gsap.to(this.angle, {
                degrees: this.convertDegrees((this.value * 360) / 100 - 1),
                delay: 0.3,
                duration: 1,
                onUpdate: () => {
                    this.path = this.describeArc(
                        this.diameter / 2,
                        this.diameter / 2,
                        this.diameter / 2 - this.strokeWidth / 2,
                        0,
                        (this.angle.degrees * 180) / Math.PI + 90
                    )
                    this.lastAngle = this.angle.degrees
                }
            })
        },

        drawBackground() {
            const canvas = this.$refs.background
            const context = canvas.getContext("2d")

            context.arc(
                this.diameter / 2,
                this.diameter / 2,
                this.diameter / 2 - this.strokeWidth / 2,
                this.convertDegrees(0),
                360
            )
            context.strokeStyle = "#eeeeee"
            context.imageSmoothingEnabled = false
            context.lineCap = "round"
            context.lineWidth = this.strokeWidth

            context.stroke()
        }
    },

    mounted() {
        this.drawBackground()

        if (this.needGraph) {
            this.animateGraph()
        }
    },

    watch: {
        value() {
            this.animateGraph()
        }
    }
}
</script>

<style scoped>
.circled-graph {
    position: relative;
}
.circled-graph canvas {
    position: absolute;
    transform: rotate(180deg);
    left: 0;
    top: 0;
}
.circled-graph svg {
    z-index: 1;
    position: absolute;
    transform: rotate(180deg);
    left: 0;
    top: 0;
}
.circled-graph p {
    margin: 0;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 13px;
    white-space: nowrap;
}
</style>
